import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { safeReturnUrl } from './return-url';
import { environment } from '../../../environments/environment';
import { AuthStore } from './auth.store';

/**
 * Attaches the Cognito ID token as a Bearer Authorization header to outbound
 * API requests. ID token is used (not access token) because the FastAPI backend
 * reads `email` from the token claims to identify the user.
 *
 * On 401, signs the user out and redirects to /login with a returnUrl so the
 * user lands back where they were after re-authenticating.
 *
 * Skipped entirely when environment.auth.bypass is true.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    if (environment.auth.bypass) return next(req);

    // Invariant: OpenApiService.buildUrl always produces `${apiHost}/api/...`,
    // so anchoring on `${apiHost}/api/` matches every backend call regardless
    // of whether apiHost is empty (same-origin prod), a path prefix (`/backend`
    // in dev), or a fully-qualified host (staging).
    const apiHost = environment.apiHost ?? '';
    const apiBase = `${apiHost}/api/`;
    const isApiCall = req.url.startsWith(apiBase);
    if (!isApiCall) return next(req);

    const auth = inject(AuthService);
    const authStore = inject(AuthStore);
    const router = inject(Router);

    const handleUnauthorized = (err: HttpErrorResponse) => {
        if (err.status !== 401) return throwError(() => err);
        // Already on the login page — don't loop.
        if (router.url.startsWith('/login')) return throwError(() => err);
        // Capture before sign-out clears state, and sanitize so a poisoned URL
        // can't propagate back into the next sign-in.
        const returnUrl = safeReturnUrl(router.url);
        auth.signOut().then(
            () => {
                authStore.clear();
                router.navigateByUrl(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
            },
            (e) => {
                console.error('[auth] signOut failed during 401 handling', e);
                authStore.clear();
                router.navigateByUrl('/login');
            },
        );
        return throwError(() => err);
    };

    return from(auth.getIdToken()).pipe(
        switchMap((token) => {
            const authReq = token
                ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
                : req;
            return next(authReq).pipe(catchError(handleUnauthorized));
        }),
    );
};
